/**
 * The open document's one stream of events: everything that tells the add-in
 * the document changed, and where, delivered to every follower in one order.
 *
 * WHY one stream
 *   Every follower used to register with Word on its own: the change ledger,
 *   the page store behind the redline cards, and the document context's
 *   bookmarking pass each added their own paragraph handlers, the page store
 *   armed the typing detector on its own, and each listened for the add-in's
 *   body replacement separately. Word ran three sets of handlers for every
 *   keystroke, and each follower re-implemented the fallbacks. Here Word is
 *   registered with once, on the first subscription, and every follower hears
 *   the same events.
 *
 * WHAT arrives (`DocumentEvent`)
 *   - word_change            -> Word's own report: paragraphs changed, added or
 *                               deleted, or a content control added, with who made it
 *   - edited_under_cursor    -> the typing detector's report, on a host without
 *                               Word's paragraph events (WordApi 1.6)
 *   - body_replaced          -> the add-in replacing the whole body, as a snapshot
 *                               restore does, "begin" then "end"
 *   - body_read              -> someone in the add-in read the whole body: the
 *                               document as it stood, for whoever follows its content
 *   - document_sent_to_agent -> a conversation's agent was handed the whole document,
 *                               so that agent's view of it is now the document itself
 *   - redlines_applied       -> the user applied cards (one card, Apply All, a review's
 *                               remedies, a comment, a reply), so the page around them is
 *                               worth reading now; it carries each card for the agent, with
 *                               the card's id and number: an edit with the paragraphs it
 *                               targets and adds and whether it applied only in part, a
 *                               comment with its paragraphs, text and quoted words, a reply
 *                               with the comment it answers and its text (`AppliedCardReport`)
 *   - pane_focused           -> the user came back to the add-in's pane from the document.
 *                               Word raises no event for a comment added, edited, resolved
 *                               or deleted, nor for a tracked deletion (measured 2026-10-15
 *                               on Word for Mac): the paragraph's text is unchanged. What
 *                               the user did in the document is worth reading now, before
 *                               they send.
 *
 * HOW the stream follows the document (`DocumentEventSource`)
 *   The source is the Office.js adapter in the add-in and a scripted fixture in
 *   tests, so the stream itself never touches Word.
 *
 * ORDER
 *   Every event is delivered with its sequence number, counted from 1 in
 *   publication order, and a body read carries the last sequence published
 *   before the read began, so a follower can tell a change the read already
 *   shows from one that came after it.
 */

import type { AppliedCardPlaceNotApplied } from "@common/types/document-changes-for-agent";

import { reportError } from "../sentry-reporter";
import { AnchorLog } from "./anchor-log";
import type { BodyReplacementPhase } from "./body-replacement";
import type { BodyRead, Unsubscribe, WordChange } from "./word-document";

/** Word's own report of a change: paragraphs changed, added or deleted, or a content control added, with who made it. */
export interface WordChangeEvent {
    kind: "word_change";
    change: WordChange;
}

/** The typing detector's report of the paragraphs under the cursor whose text changed, on a host without Word's paragraph events (WordApi 1.6). */
export interface EditedUnderCursorEvent {
    kind: "edited_under_cursor";
    uniqueLocalIds: string[];
}

/** The add-in replacing the whole body, as a snapshot restore does: "begin" before the write, "end" after it. */
export interface BodyReplacedEvent {
    kind: "body_replaced";
    phase: BodyReplacementPhase;
}

/** Someone in the add-in read the whole body: the document as it stood, for whoever follows its content. */
export interface BodyReadEvent {
    kind: "body_read";
    read: BodyRead;
}

/** A conversation's agent was handed the whole document, so that agent's view of it is now the document itself. */
export interface DocumentSentToAgentEvent {
    kind: "document_sent_to_agent";
    sentDocument: DocumentSentToAgent;
}

/**
 * What every report of an applied card names: the card it belongs to and the number the
 * user sees on it.
 *
 * NULL `cardId`
 *   A write that names no card: a rule page card, or a review's remedy or comment applied
 *   on its own (`applyRemedy`). Its reports are never gathered with another's. An Apply
 *   All run names every write by its rule, so a review's rule gathers its own remedy and
 *   comment.
 *
 * NULL `cardNumber`
 *   The card carries no number: a card no chat card stands for, or one stored before
 *   cards were numbered.
 */
export interface AppliedCardIdentity {
    cardId: string | null;
    cardNumber: number | null;
}

/**
 * An edit card the user applied: the paragraphs it targets, the paragraphs it added
 * under the ids it wrote them with, and whether it landed only in part.
 *
 * EMPTY `addedParagraphBookmarkNames`
 *   The card added no paragraph, or it was proposed before a new paragraph carried an id.
 *
 * EMPTY `tableChangeSentences`
 *   The card adds and removes no table row and no table. Otherwise each row or table change
 *   in the words the card shows (`TableChangePreview.lines`): new cells carry no id the page
 *   can name them by, so the sentence is how the agent hears of them with their card.
 */
export interface AppliedEditReport extends AppliedCardIdentity {
    kind: "edit";
    paragraphBookmarkNames: string[];
    addedParagraphBookmarkNames: string[];
    tableChangeSentences: string[];
    appliedInPart: boolean;
}

/**
 * A comment written in the document: a comment card, or one of an edit card's own
 * comments, whose `cardId` is then that edit card's.
 *
 * NULL `quotedText`
 *   The comment sits on its paragraph's first word or on the whole paragraph: it quotes
 *   no words.
 *
 * NULL `notWrittenReason`
 *   The comment is in the document. A reason ("Word refused it", "its paragraph is not in
 *   the document") is given only for an edit card's own comment the card's write could not
 *   add, so the agent hears of the comment the card shows as not added.
 */
export interface AppliedCommentReport extends AppliedCardIdentity {
    kind: "comment";
    paragraphBookmarkNames: string[];
    text: string;
    quotedText: string | null;
    notWrittenReason: string | null;
}

/** A reply written to a comment: the id the agent read the comment by, and the reply's text. */
export interface AppliedReplyReport extends AppliedCardIdentity {
    kind: "reply";
    commentId: string;
    text: string;
}

/**
 * A place of a change-throughout card an Apply All run did not write, while another
 * place of the card was written: the paragraph the place is in, and whether it was not
 * chosen or not applied, so the agent hears the card landed in part and where not.
 *
 * EXAMPLE
 *   card #9 "Licensor" -> "Provider", its place on _keyword_height_color dropped by the
 *   contested reconcile
 *   -> { place_not_applied, msg-9, #9, [_keyword_height_color], "not_chosen" }
 */
export interface AppliedPlaceNotAppliedReport extends AppliedCardIdentity {
    kind: "place_not_applied";
    paragraphBookmarkNames: string[];
    outcome: AppliedCardPlaceNotApplied["outcome"];
}

/**
 * One applied card as the add-in reports it, before the agents' views read the page
 * for its paragraphs (`AgentDocumentViews`).
 *
 * WHY reports and not the cards the agent is told of
 *   The paragraphs' text before and after the apply is known only to a view: before is
 *   what the conversation's agent last read, after is the page the view reads once the
 *   apply is reported. A report names the paragraphs; each view fills in their text.
 */
export type AppliedCardReport = AppliedEditReport | AppliedCommentReport | AppliedReplyReport | AppliedPlaceNotAppliedReport;

/** The user applied cards, so the page around them is worth reading now. */
export interface RedlinesAppliedEvent {
    kind: "redlines_applied";
    /** Each card the user applied, in the order it was applied, as the add-in reports it (`AppliedCardReport`). */
    appliedCards: AppliedCardReport[];
}

/** The user came back to the add-in's pane from the document, where a comment or a tracked deletion raised no Word event. */
export interface PaneFocusedEvent {
    kind: "pane_focused";
}

/** One event on the document's stream; the module header lists what each one means. */
export type DocumentEvent =
    | WordChangeEvent
    | EditedUnderCursorEvent
    | BodyReplacedEvent
    | BodyReadEvent
    | DocumentSentToAgentEvent
    | RedlinesAppliedEvent
    | PaneFocusedEvent;

/**
 * The whole document as a conversation's agent was handed it.
 *
 * `coversEventsThroughSequence` is the stream's last sequence when the copy was
 * read, taken before the read began (`DocumentEvents.lastEventSequence`): the
 * agent's copy shows every change published at or before it; a change published
 * during or after the read is one the agent may not have seen.
 */
export interface DocumentSentToAgent {
    conversationId: string;
    coversEventsThroughSequence: number;
}

/** Where an event stands in the stream: its sequence number, counted from 1 in publication order. */
export interface DocumentEventDelivery {
    sequence: number;
}

export type DocumentEventListener = (event: DocumentEvent, delivery: DocumentEventDelivery) => void;

/** What the stream needs from the host to follow the document. */
export interface DocumentEventSource {
    /** Whether the host raises Word's paragraph events (WordApi 1.6). */
    wordChangesSupported(): boolean;
    /** One registration of Word's change events; rejects when the host refuses it. */
    registerWordChanges(handler: (change: WordChange) => void): Promise<Unsubscribe>;
    /** Arms the typing detector, which reports paragraphs under the cursor whose text changed; throws when there is no Office to arm it on. */
    followEditsUnderCursor(handler: (uniqueLocalIds: string[]) => void): void;
    /**
     * The add-in replacing the whole body: "begin" before the write, "end" after
     * it. Every `uniqueLocalId` held before "end" names a paragraph that no longer
     * exists; bookmarks return with the file.
     */
    onBodyReplaced(handler: (phase: BodyReplacementPhase) => void): () => void;
    /** The user bringing the add-in's pane into focus, from the document or anywhere else. */
    onPaneFocused(handler: () => void): () => void;
}

export class DocumentEvents {
    private readonly eventListeners = new Set<DocumentEventListener>();
    /** False until the first subscription; the stream follows the document from then on, for the life of the add-in. */
    private followingDocument = false;
    /**
     * The unsubscribe of Word's change registration while it stands, a promise
     * whether the registration is still in flight or resolved; null before it,
     * after a body replacement began, or once it failed. Its identity tells the
     * live registration's events and refusal from a dropped one's.
     */
    private pendingWordChangeUnsubscribe: Promise<Unsubscribe> | null = null;
    /** True once the typing detector stands in for Word's change events. */
    private followingEditsUnderCursor = false;
    /** The sequence number of the last event published; 0 before the first. */
    private lastPublishedSequence = 0;
    /** The sequence number of the last event that reported the document's content changed; 0 before the first. */
    private lastContentChangeSequence = 0;

    constructor(private readonly eventSource: DocumentEventSource) {}

    /**
     * The sequence number of the last event published, 0 before the first. A whole-body
     * reader takes it as the read begins (`BodyRead.coversEventsThroughSequence`): every
     * change published at or before it is in the read.
     */
    get lastEventSequence(): number {
        return this.lastPublishedSequence;
    }

    /**
     * Whether the document's content changed after a whole-body read began: the user typed,
     * a co-author edited, or the add-in replaced the body, at a sequence past the read's.
     *
     * WHY not `lastEventSequence` alone
     *   Every event moves it, a whole-body read included, so it cannot say whether the text
     *   a read holds is still the text in the document.
     *
     * EXAMPLE
     *   a recovery reads the body at sequence 7; a body read of another reader is published as 8
     *   -> false: no content moved
     *   the user types in a paragraph, published as 9 -> true: the read may no longer hold it
     */
    contentChangedSince(coversEventsThroughSequence: number): boolean {
        return this.lastContentChangeSequence > coversEventsThroughSequence;
    }

    /** Tells every follower someone read the whole body; the read is the document as it stood. */
    publishBodyRead(read: BodyRead): void {
        this.publish({ kind: "body_read", read });
    }

    /**
     * Tells every follower a conversation's agent was handed the whole document: the
     * first message's attachment, or the agent's own request for the document.
     */
    publishDocumentSentToAgent(sentDocument: DocumentSentToAgent): void {
        this.publish({ kind: "document_sent_to_agent", sentDocument });
    }

    /** Tells every follower the user applied cards, with each card as the add-in reports it. */
    publishRedlinesApplied(appliedCards: AppliedCardReport[]): void {
        this.publish({ kind: "redlines_applied", appliedCards });
    }

    /**
     * Calls the listener with every event from now on; returns the unsubscribe.
     *
     * WHEN this is the stream's first subscription
     *   CONSEQUENCE
     *     the stream starts following the document: it listens for body
     *     replacements and registers with Word, or arms the typing detector on a
     *     host without Word's paragraph events. A listener that unsubscribes
     *     leaves the stream following for the others and for the next.
     */
    subscribe(listener: DocumentEventListener): () => void {
        this.eventListeners.add(listener);
        this.followDocument();
        return () => {
            this.eventListeners.delete(listener);
        };
    }

    /** Whether Word itself reports changes on this host; false means the typing detector stands in. */
    wordChangesSupported(): boolean {
        return this.eventSource.wordChangesSupported();
    }

    /**
     * Numbers the event and hands it to every listener, in subscription order.
     *
     * WHEN a listener throws
     *   CONSEQUENCE
     *     the error goes to Sentry and the next listener still hears the event: one
     *     follower failing never leaves the redline cards or the ledger behind,
     *     as it could not when each follower held its own Word registration.
     *
     * EXAMPLE
     *   lastPublishedSequence = 4 -> the event is delivered as sequence 4 + 1 = 5
     */
    private publish(event: DocumentEvent): void {
        this.lastPublishedSequence += 1;
        /**
         * CASES
         *   - a Word change, an edit under the cursor, or a body replacement -> the content moved at this sequence
         *   - any other event (a read, a focus, an apply report)             -> the content is as it was
         */
        if (event.kind === "word_change" || event.kind === "edited_under_cursor" || event.kind === "body_replaced") this.lastContentChangeSequence = this.lastPublishedSequence;
        const delivery: DocumentEventDelivery = { sequence: this.lastPublishedSequence };
        for (const listener of Array.from(this.eventListeners)) {
            try {
                listener(event, delivery);
            } catch (error) {
                reportError(error, { tags: { component: "DocumentEvents", action: `document_event_listener:${event.kind}` } });
            }
        }
    }

    /** Starts following the document once: the body replacements, the pane's focus, then Word's change events. */
    private followDocument(): void {
        /**
         * WHEN an earlier subscription already started following the document
         *   CONSEQUENCE
         *     nothing more is registered: every follower shares the one registration.
         */
        if (this.followingDocument) return;
        this.followingDocument = true;
        this.eventSource.onBodyReplaced((phase) => this.onBodyReplaced(phase));
        this.eventSource.onPaneFocused(() => this.publish({ kind: "pane_focused" }));
        this.followWordChanges();
    }

    /**
     * Registers with Word's change events, the one registration every follower shares.
     *
     * CASES
     *   - the host has no paragraph events      -> the typing detector stands in
     *   - the registration is refused           -> the typing detector stands in
     *   - a refusal arrives for a registration
     *     a body replacement already replaced   -> ignored: the newer registration is the live one
     *   - Word delivers an event to a
     *     registration a body replacement
     *     dropped, before its removal synced    -> not published: it reports the replacement's
     *                                              own write, on ids the old body took with it
     */
    private followWordChanges(): void {
        if (!this.eventSource.wordChangesSupported()) {
            this.followEditsUnderCursor();
            return;
        }
        const requestedWordChangeUnsubscribe: Promise<Unsubscribe> = this.eventSource
            .registerWordChanges((change) => {
                if (this.pendingWordChangeUnsubscribe !== requestedWordChangeUnsubscribe) return;
                this.publish({ kind: "word_change", change });
            })
            .catch((error: unknown) => {
                reportError(error, { level: "warning", tags: { component: "DocumentEvents", action: "register_word_events" } });
                /**
                 * WHEN this refusal belongs to a registration a body replacement already dropped
                 *   CONSEQUENCE
                 *     the live registration that replaced it stays; nothing falls back.
                 */
                if (this.pendingWordChangeUnsubscribe === requestedWordChangeUnsubscribe) {
                    this.pendingWordChangeUnsubscribe = null;
                    this.followEditsUnderCursor();
                }
                return async () => undefined;
            });
        this.pendingWordChangeUnsubscribe = requestedWordChangeUnsubscribe;
    }

    /**
     * Arms the typing detector once, for a host where Word reports no paragraph changes.
     *
     * WHEN the host has no Office object to arm the detector on (a page outside Word, a test)
     *   CONSEQUENCE
     *     nothing reports edits; followers learn of changes only from their own reads.
     */
    private followEditsUnderCursor(): void {
        /**
         * WHEN the detector already stands in for Word's change events
         *   CONSEQUENCE
         *     it is not armed twice: one selection read per cursor move is enough.
         */
        if (this.followingEditsUnderCursor) return;
        try {
            this.eventSource.followEditsUnderCursor((uniqueLocalIds) => this.publish({ kind: "edited_under_cursor", uniqueLocalIds }));
            this.followingEditsUnderCursor = true;
        } catch (error) {
            AnchorLog.record({ kind: "error", bookmarkNames: [], step: "follow edits under cursor", message: AnchorLog.messageOf(error) });
        }
    }

    /**
     * The add-in replacing the whole body.
     *
     * WHEN the replacement begins
     *   CONSEQUENCE
     *     Word's registration made against the old body is dropped before the
     *     followers hear "begin", so none of them records the replacement's own
     *     flood of added and deleted paragraphs, ids that die with the old body.
     *
     * WHEN the replacement ends
     *   CONSEQUENCE
     *     the stream registers with Word again before the followers hear "end", so
     *     an edit to the restored body is reported from its first keystroke. With
     *     the typing detector standing in, nothing is registered again: it follows
     *     the cursor, which outlives the body.
     */
    private onBodyReplaced(phase: BodyReplacementPhase): void {
        if (phase === "begin") {
            const droppedWordChangeUnsubscribe = this.pendingWordChangeUnsubscribe;
            this.pendingWordChangeUnsubscribe = null;
            if (droppedWordChangeUnsubscribe !== null) void droppedWordChangeUnsubscribe.then((unsubscribe) => unsubscribe());
        }
        if (phase === "end" && this.pendingWordChangeUnsubscribe === null && !this.followingEditsUnderCursor) this.followWordChanges();
        this.publish({ kind: "body_replaced", phase });
    }
}
